// @ts-check
import moment from "moment";
import { API_DATE_FORMAT } from "src/consts/global";

/**
 * The reference date is a PLANNER concept: "compare the plan against what was
 * actually allocated on this date". Forecast has no reference-date UI at all,
 * so it must never inherit a value chosen on another page (MS-1040).
 *
 * What is persisted is the user's SELECTION, not the date it resolved to. A
 * literal date froze "Today" at whatever day it was when the user picked it:
 * two days later the planner still read that date, the picker could no longer
 * match it to Today/start/end so it re-rendered as "Custom date", and forecast
 * inherited it as the anchor for its actuals.
 */

export const REFERENCE_DATE_STORAGE_KEY = "forecastReferenceDate";

export const DATE_OPTIONS = {
  TODAY: "today",
  ALLOCATION_PROJECT_START_DATE: "allocationProjectStartDate",
  ALLOCATION_PROJECT_END_DATE: "allocationProjectEndDate",
  CHOSEN_DAY: "chosenDay",
};

const KNOWN_OPTIONS = new Set(Object.values(DATE_OPTIONS));

const formatDate = (value) =>
  moment(value).startOf("day").format(API_DATE_FORMAT);

/**
 * moment formats an unparseable value as the literal string "Invalid date"
 * rather than throwing, so a bad project date has to be caught here or that
 * string reaches the query variables and the column tooltip.
 */
const formatValidDate = (value) => {
  const parsed = moment(value);

  return value && parsed.isValid()
    ? parsed.startOf("day").format(API_DATE_FORMAT)
    : null;
};

/** Project dates are absolute instants; read the UTC day, not the local one. */
const formatValidUtcDate = (value) => {
  const parsed = moment.utc(value);

  return value && parsed.isValid()
    ? parsed.startOf("day").format(API_DATE_FORMAT)
    : null;
};

/**
 * Resolves a selection to a concrete date. "Today" resolves at call time, which
 * is the whole point: it must mean today whenever it is read, not the day it
 * was chosen.
 *
 * @param {{ option?: string, date?: string }} selection
 * @param {{ startDate?: any, allocationDate?: any }} [allocationProject]
 * @returns {string|null} API-formatted date, or null if it cannot be resolved
 */
export const resolveReferenceDateSelection = (
  selection,
  allocationProject = {}
) => {
  if (!selection?.option) {
    return null;
  }

  switch (selection.option) {
    case DATE_OPTIONS.TODAY:
      return formatDate(moment());
    case DATE_OPTIONS.ALLOCATION_PROJECT_START_DATE:
      return formatValidUtcDate(allocationProject.startDate);
    case DATE_OPTIONS.ALLOCATION_PROJECT_END_DATE:
      return formatValidUtcDate(allocationProject.allocationDate);
    case DATE_OPTIONS.CHOSEN_DAY:
      return formatValidDate(selection.date);
    default:
      return null;
  }
};

/**
 * Which radio a stored selection should show as. A custom date is the only
 * option that carries a date of its own.
 *
 * @param {{ option?: string, date?: string }} [selection]
 * @returns {{ option: string, date?: string }}
 */
export const selectionForOption = (option, targetDate) =>
  option === DATE_OPTIONS.CHOSEN_DAY
    ? { option, date: formatDate(targetDate) }
    : { option };

/**
 * Whether two selections mean the same thing.
 *
 * Callers must compare SELECTIONS, not the dates they resolve to. Two
 * different options can resolve to the same day today and diverge tomorrow —
 * "Today" and a planning period that starts today, for instance — so treating
 * an unchanged date as an unchanged choice silently discards the new option.
 *
 * @param {{ option?: string, date?: string }|null} [a]
 * @param {{ option?: string, date?: string }|null} [b]
 * @returns {boolean}
 */
export const isSameReferenceDateSelection = (a, b) => {
  if (!a || !b) {
    return false;
  }

  if (a.option !== b.option) {
    return false;
  }

  // Only a custom date carries a date of its own; for every other option the
  // date is derived, so comparing it would report a spurious difference.
  return a.option === DATE_OPTIONS.CHOSEN_DAY ? a.date === b.date : true;
};

/**
 * @returns {{ option: string, date?: string }|null}
 */
export const readReferenceDateSelection = () => {
  let raw;
  try {
    raw = localStorage.getItem(REFERENCE_DATE_STORAGE_KEY);
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return null;
  }

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    // Reject an unrecognised option rather than handing it back: callers seed
    // the picker's radio from this, and an unknown option leaves every radio
    // unchecked with no way to tell what is in effect.
    if (parsed?.option && KNOWN_OPTIONS.has(parsed.option)) {
      return parsed;
    }
    if (parsed?.option) {
      return null;
    }
  } catch {
    // Not JSON: a bare date string written before selections were stored.
    // Treat it as the custom date it literally is rather than discarding the
    // user's choice — a legacy frozen "Today" is indistinguishable from a
    // deliberate custom date, and it is what the planner is showing today.
  }

  const legacyDate = formatValidDate(raw);

  return legacyDate
    ? { option: DATE_OPTIONS.CHOSEN_DAY, date: legacyDate }
    : null;
};

/**
 * @param {{ option: string, date?: string }} selection
 */
export const writeReferenceDateSelection = (selection) => {
  try {
    localStorage.setItem(REFERENCE_DATE_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Persisting the selection is a convenience; losing it must not break the
    // page the user is on.
  }
};

/**
 * The persisted reference date for a page, or null when there is nothing
 * usable. Callers fall back to their own default (the prefill date).
 *
 * @param {{ startDate?: any, allocationDate?: any }} [allocationProject]
 * @returns {string|null}
 */
export const readStoredReferenceDate = (allocationProject) =>
  resolveReferenceDateSelection(
    readReferenceDateSelection() ?? {},
    allocationProject
  );
